import { VendorAvatarWithDetails } from '@melio/ap-widgets';
import {
  BillSubscriptionIntervalTypeEnum,
  BillSubscriptionManagedBy,
  PaymentFullyExpanded,
  useBill,
} from '@melio/platform-api';
import { useMelioIntl } from '@melio/platform-i18n';
import { RefObject } from 'react';

import { useIsMobile } from '../../../utils/viewport-utils/useIsMobile';

const INTERVAL_TYPE_HEADER_DISPLAY_NAMES = {
  [BillSubscriptionIntervalTypeEnum.Weekly]: 'Weekly',
  [BillSubscriptionIntervalTypeEnum.Every2Weeks]: 'Every 2 weeks',
  [BillSubscriptionIntervalTypeEnum.Every4Weeks]: 'Every 4 weeks',
  [BillSubscriptionIntervalTypeEnum.Monthly]: 'Monthly',
  [BillSubscriptionIntervalTypeEnum.TwiceAMonth]: 'Twice a month',
  [BillSubscriptionIntervalTypeEnum.Every2Months]: 'Every 2 months',
  [BillSubscriptionIntervalTypeEnum.Every3Months]: 'Every 3 months',
  [BillSubscriptionIntervalTypeEnum.Every4Months]: 'Every 4 months',
  [BillSubscriptionIntervalTypeEnum.Every6Months]: 'Every 6 months',
  [BillSubscriptionIntervalTypeEnum.Yearly]: 'Yearly',
};

export const Header = ({
  payment,
  billDetailsRef,
}: {
  payment: PaymentFullyExpanded;
  billDetailsRef: RefObject<HTMLDivElement>;
}) => {
  const isMobile = useIsMobile();
  const { formatMessage } = useMelioIntl();
  const { data: bill } = useBill({
    id: payment.paymentBillsInfo?.[0]?.id,
  });

  const getRecurringPaymentDescriptionLabel = (
    intervalType: BillSubscriptionIntervalTypeEnum,
    occurrenceNumber: number,
    managedBy: BillSubscriptionManagedBy | null,
    numOfOccurrences?: number | null
  ) => {
    const formatMessageParams = {
      isMobile,
      intervalType: INTERVAL_TYPE_HEADER_DISPLAY_NAMES[intervalType],
      numOfOccurrencesText: numOfOccurrences || 'ongoing',
      occurrenceNumber,
    };
    return managedBy === BillSubscriptionManagedBy.Fiserv
      ? formatMessage(
          'activities.payDashboard.drawer.header.payment.description.recurring.managedByFiserv',
          formatMessageParams
        )
      : formatMessage('activities.payDashboard.drawer.header.payment.description.recurring', formatMessageParams);
  };

  const getDescription = () => {
    if (!payment.subscriptionOccurrence) {
      if (bill?.invoice.number) {
        return formatMessage('activities.payDashboard.drawer.header.payment.description.invoiceNumber', {
          invoiceNumber: bill.invoice.number,
        });
      }
      return formatMessage('activities.payDashboard.drawer.header.payment.description.noInvoiceNumber');
    }

    const {
      subscriptionOccurrence: {
        billSubscription: { intervalType, numOfOccurrences, managedBy },
        occurrenceNumber,
      },
    } = payment;

    return getRecurringPaymentDescriptionLabel(intervalType, occurrenceNumber, managedBy, numOfOccurrences);
  };

  return (
    <VendorAvatarWithDetails
      vendor={payment.vendor}
      data-testid="pay-dashboard-payments-tab-drawer-header"
      description={getDescription()}
      actionProps={
        !isMobile
          ? {
              label: formatMessage('activities.payDashboard.drawer.header.payment.action.label', {
                billsCount: payment.bills?.length,
              }),
              onClick: () => {
                billDetailsRef.current?.scrollIntoView({ behavior: 'smooth' });
              },
              'data-testid': 'view-bill-details-button',
            }
          : undefined
      }
    />
  );
};
